import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle2, Clock3, FileText, Bell, AlertCircle, ChevronRight, CircleAlert, CircleX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchCurrentUserBookings } from '../lib/bookingService';
import { formatTimeAgo } from '../lib/dateTime';
import { buildBookingNotification } from '../lib/notificationService';
import BookingPagination from '../components/BookingPagination';
import { BusyTable } from '../components/TableBusyOverlay';
import usePagedListUi from '../hooks/usePagedListUi';

const PAGE_SIZE = 5;

const READ_STATE_KEY = 'echo_jham_booking_notifications_read_v1';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'updates', label: 'Updates' },
];

const CATEGORY_CONFIG = {
  agreement: {
    icon: FileText,
    iconBg: 'bg-orange-500',
    border: 'border-orange-500/50',
    title: 'Agreement Confirmation Required',
    action: 'Tap to review',
  },
  proposal: {
    icon: FileText,
    iconBg: 'bg-amber-500',
    border: 'border-amber-500/50',
    title: 'Proposal & Quotation Ready',
    action: 'Tap to review',
  },
  consultation: {
    icon: Clock3,
    iconBg: 'bg-emerald-600',
    border: 'border-emerald-500/40',
    title: 'Initial Consultation Scheduled',
    action: 'View details',
  },
  accepted: {
    icon: CheckCircle2,
    iconBg: 'bg-emerald-600',
    border: 'border-emerald-500/40',
    title: 'Booking Accepted',
    action: 'View booking progress',
  },
  submitted: {
    icon: Bell,
    iconBg: 'bg-slate-500',
    border: 'border-slate-500/30',
    title: 'Booking Submitted',
    action: 'View booking',
  },
  review: {
    icon: AlertCircle,
    iconBg: 'bg-blue-500',
    border: 'border-blue-500/40',
    title: 'Admin Review in Progress',
    action: 'View booking progress',
  },
  declined: {
    icon: CircleX,
    iconBg: 'bg-red-600',
    border: 'border-red-500/40',
    title: 'Booking Declined',
    action: 'View booking',
  },
  updates: {
    icon: CircleAlert,
    iconBg: 'bg-slate-500',
    border: 'border-slate-500/30',
    title: 'Booking Update',
    action: 'View booking',
  },
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const getCategory = (item) => {
  const title = normalizeText(item.title);
  const body = normalizeText(item.body);
  const haystack = `${title} ${body}`;

  if (haystack.includes('declined') || haystack.includes('rejected')) return 'declined';
  if (haystack.includes('agreement')) return 'agreement';
  if (haystack.includes('proposal')) return 'proposal';
  if (haystack.includes('consultation')) return 'consultation';
  if (haystack.includes('accepted') || haystack.includes('approved')) return 'accepted';
  if (haystack.includes('review')) return 'review';
  if (haystack.includes('submitted')) return 'submitted';
  return 'updates';
};

const getNotificationReadMap = () => {
  try {
    const raw = localStorage.getItem(READ_STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const setNotificationReadMap = (next) => {
  try {
    localStorage.setItem(READ_STATE_KEY, JSON.stringify(next || {}));
  } catch {
    // Ignore storage failures.
  }
};

const makeStableId = (item) => item.id || `${item.bookingId || 'booking'}-${item.createdAt?.getTime?.() || Date.now()}`;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [readMap, setReadMap] = useState(() => getNotificationReadMap());
  const [currentPage, setCurrentPage] = useState(1);
  const listTopRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const loadNotifications = async () => {
      try {
        const { bookings } = await fetchCurrentUserBookings();
        if (!isMounted) return;

        const seeded = (bookings || [])
          .map((booking) => {
            const notification = buildBookingNotification(booking, { idPrefix: 'notif' });
            if (!notification) return null;

            const category = getCategory(notification);
            const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.updates;

            return {
              id: makeStableId(notification),
              bookingId: notification.bookingId,
              eventName: notification.eventName,
              title: config.title,
              body: notification.body,
              createdAt: notification.createdAt,
              category,
              action: config.action,
              icon: config.icon,
              iconBg: config.iconBg,
              border: config.border,
            };
          })
          .filter(Boolean)
          .sort((left, right) => right.createdAt.getTime() - left.createdAt.getTime());

        setNotifications(seeded);
      } catch (error) {
        console.error('Failed to load booking notifications:', error);
        if (isMounted) {
          setNotifications([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadNotifications();

    return () => {
      isMounted = false;
    };
  }, []);

  const markRead = (item) => {
    const next = { ...readMap, [item.id]: true };
    setReadMap(next);
    setNotificationReadMap(next);
  };

  const markAllAsRead = () => {
    const next = notifications.reduce((accumulator, item) => {
      accumulator[item.id] = true;
      return accumulator;
    }, {});
    setReadMap(next);
    setNotificationReadMap(next);
  };

  const unreadCount = notifications.filter((item) => !readMap[item.id]).length;

  const filteredNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return notifications.filter((item) => !readMap[item.id]);
    }

    if (activeTab === 'updates') {
      return notifications.filter((item) => ['consultation', 'proposal', 'agreement', 'accepted', 'review', 'declined'].includes(item.category));
    }

    return notifications;
  }, [activeTab, notifications, readMap]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / PAGE_SIZE));
  const visibleNotifications = filteredNotifications.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  const resolveRoute = (item) => item.bookingId ? `/book/progress/${item.bookingId}` : '/book';

  return (
    <div className="min-h-[calc(100vh-11rem)] bg-[#141414] text-white pb-24 md:pb-28">
      <div className="mx-auto w-full max-w-[420px] px-4 pt-4 pb-8">
        <div className="flex items-center justify-between gap-3 mb-5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-black/60 text-white"
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>

          <div className="text-center flex-1">
            <h1 className="text-xl font-semibold text-white">Notifications</h1>
          </div>

          <button
            type="button"
            onClick={markAllAsRead}
            className="text-sm font-semibold text-[#b31a1a] hover:text-[#dc2626]"
          >
            Mark all as read
          </button>
        </div>

        <div className="flex items-center gap-3 mb-5 overflow-x-auto pb-1">
          {TABS.map((tab) => {
            const count = tab.key === 'all' ? notifications.length : tab.key === 'unread' ? unreadCount : notifications.filter((item) => ['consultation', 'proposal', 'agreement', 'accepted', 'review', 'declined'].includes(item.category)).length;
            const isActive = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition whitespace-nowrap ${
                  isActive ? 'bg-[#b31a1a] text-white' : 'bg-black/70 text-white/70 border border-white/10'
                }`}
              >
                <span>{tab.label}</span>
                {tab.key !== 'all' && (
                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${isActive ? 'bg-white/15' : 'bg-white/10'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {loading && filteredNotifications.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-black/70 px-4 py-8 text-center text-sm text-white/50">
            Loading...
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-black/70 px-4 py-10 text-center">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-white/5 border border-white/10 text-white/60">
              <Bell size={26} />
            </div>
            <p className="text-xl font-bold text-white mb-1">No notifications yet</p>
            <p className="text-sm text-white/50">Your booking updates will appear here.</p>
          </div>
        ) : (
          <div ref={listTopRef} className="space-y-4">
            <BusyTable show={showSkeleton} tone="public">
            {visibleNotifications.map((item) => {
              const Icon = item.icon;
              const isRead = Boolean(readMap[item.id]);

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    markRead(item);
                    navigate(resolveRoute(item));
                  }}
                  className={`w-full rounded-[22px] border bg-black/80 px-4 py-4 text-left shadow-[0_10px_24px_rgba(0,0,0,0.18)] transition hover:border-white/20 ${item.border} ${isRead ? 'opacity-80' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${item.iconBg} text-white shadow-sm`}>
                      <Icon size={22} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold text-white">{item.title}</p>
                          <p className="mt-1 text-xs leading-relaxed text-slate-400">{item.body}</p>
                        </div>
                        {!isRead && <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#b31a1a] shrink-0" />}
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-3">
                        <p className="text-xs text-slate-500">
                          {formatTimeAgo(item.createdAt)} • {item.eventName || 'Booking update'}
                        </p>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#b31a1a]">
                          {item.action}
                          <ChevronRight size={14} />
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
            </BusyTable>
            <BookingPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
